import { tr } from '@/lib/i18n-core';
import { useState, type ReactNode } from 'react';
import { AlertTriangle, Check, Copy, Info, KeyRound, RefreshCw, Search } from 'lucide-react';
import { compareVersions, LOCK_MESSAGES, staleWarningMessage, type LocalUpdateStatusDto } from '@his/shared';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime , copyText } from '@/lib/utils';
import { useCheckForUpdatesMutation, useOfflineAccessQuery, useRegenerateDownloadPinMutation, useUpdateStatusQuery } from '@/lib/updates-api';
import { Alert, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Spinner, statusLabel } from '@/components/ui/primitives';
import { ReleaseNotes } from '@/components/ui/release-notes';
import { useToast } from '@/components/ui/toast';
import { UpdateApplyDialog } from '@/components/update-flow';
import { visibleInterval } from '@/lib/polling';
import { OnlineCopyCard } from './OnlineCopyCard';
import { LicenceCard } from './LicenceCard';
import { DataExportCard } from './DataExportCard';
import { SupportAccessCard } from './SupportAccessCard';
import { UpdateManagement } from './UpdateManagement';

const LOCK_BADGE: Record<LocalUpdateStatusDto['lockState'], { variant: 'success' | 'warning' | 'destructive'; label: string }> = {
  NONE: { variant: 'success', label: 'Writable' },
  STALE: { variant: 'warning', label: 'Read-only (stale)' },
  MISMATCH: { variant: 'destructive', label: 'Read-only (machine mismatch)' },
  VERSION: { variant: 'destructive', label: 'Read-only (update required)' },
};

const ACTIVATION_LABEL: Record<LocalUpdateStatusDto['activationState'], string> = {
  NOT_ACTIVATED: 'Not activated',
  ACTIVE: 'Activated',
  MISMATCH: 'Fingerprint mismatch',
  RETIRED: 'Retired',
};

const SOURCE_LABEL: Record<string, string> = {
  env: 'APP_VERSION environment variable',
  'install-manifest': 'Installed package manifest',
  'package-manifest': 'Package manifest',
  'package.json': 'package.json',
  fallback: 'Development build',
};

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[150px_1fr] items-start gap-3 py-2 text-sm">
      <span className="pt-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-7 w-7"
      aria-label={tr('admin', 'copy_x', { label: label })}
      onClick={() => {
        void copyText(value).then((ok) => {
          if (!ok) return;
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1_800);
        });
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}

function formatWhen(iso: string | null): string {
  return iso ? dateTime(iso) : 'Never';
}

/**
 * Settings → About (plan §3.7): what this server runs and where that came
 * from, the licence check-in that keeps it writable, the newest version the
 * cloud has offered, and the two actions an administrator has — check now,
 * update now — plus the landing-page download PIN.
 */
export function AboutTab({ canManage, tenant }: { canManage: boolean; tenant: { name: string; slug: string } | null }) {
  const toast = useToast();
  const { isSuperAdmin, can } = useAuth();
  const statusQuery = useUpdateStatusQuery({ refetchInterval: visibleInterval(60_000) });
  const checkNow = useCheckForUpdatesMutation();
  const regeneratePin = useRegenerateDownloadPinMutation();
  const offlineAccess = useOfflineAccessQuery();
  const [pin, setPin] = useState<string | null>(null);
  const [applyOpen, setApplyOpen] = useState(false);

  const status = statusQuery.data;

  if (statusQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Spinner /> {tr('admin', 'reading_the_servers_version')}
      </div>
    );
  }

  if (!status) {
    return (
      <Alert variant="warning" className="flex items-center gap-3">
        <AlertTriangle className="h-4 w-4 flex-none" aria-hidden />
        <span className="flex-1">{tr('admin', 'could_not_read_the_servers_version_status')}</span>
        <Button variant="outline" size="sm" onClick={() => void statusQuery.refetch()}>
          {tr('admin', 'retry')}
        </Button>
      </Alert>
    );
  }

  const isLocal = status.deploymentMode === 'LOCAL';
  const pending = status.pendingUpdate;
  const versionBlocked = pending?.isRequired && pending.minimumAllowedVersion !== null && compareVersions(status.runningVersion, pending.minimumAllowedVersion) < 0;
  const lock = LOCK_BADGE[status.lockState];
  // Owner toggle "Allow offline mode": until it is on there is no PIN to hand out.
  const offlineBlocked = offlineAccess.data?.offlineEnabled === false;

  const runCheck = (): void => {
    checkNow.mutate(undefined, {
      onSuccess: (fresh) => {
        if (fresh.pendingUpdate) toast.success(tr('admin', 'version_x_is_available', { version: fresh.pendingUpdate.version }));
        else toast.success(tr('admin', 'this_server_is_up_to_date'));
      },
      onError: (err) => toast.error(tr('admin', 'could_not_check_for_updates'), describeError(err)),
    });
  };

  const runRegeneratePin = (): void => {
    regeneratePin.mutate(undefined, {
      onSuccess: (result) => {
        setPin(result.pin);
        toast.success(tr('admin', 'new_download_pin_generated'), tr('admin', 'the_previous_pin_no_longer_works'));
      },
      onError: (err) => toast.error(tr('admin', 'could_not_generate_a_download_pin'), describeError(err)),
    });
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {isLocal && status.syncHealth && <OnlineCopyCard health={status.syncHealth} canManage={canManage} />}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Info className="h-4 w-4 text-primary" aria-hidden /> {tr('admin', 'this_server')}
          </CardTitle>
          <CardDescription>{tr('admin', 'what_is_running_here_and_where_it_came_from')}</CardDescription>
        </CardHeader>
        <CardContent className="divide-y">
          <Row label={tr('admin', 'version')}>
            <span className="font-mono text-base font-semibold">{status.runningVersion}</span>
            <Badge variant={status.channel === 'BETA' ? 'warning' : 'default'} className="ms-2">
              {statusLabel(status.channel)}
            </Badge>
          </Row>
          <Row label={tr('admin', 'deployment')}>{isLocal ? tr('admin', 'offline_local_server') : tr('admin', 'online_cloud')}</Row>
          <Row label={tr('admin', 'version_source')}>{SOURCE_LABEL[status.build.source] ?? status.build.source}</Row>
          <Row label={tr('admin', 'build')}>
            <span className="font-mono text-xs text-muted-foreground">{status.build.gitSha ? status.build.gitSha.slice(0, 12) : '—'}</span>
            {status.build.builtAt && <span className="ms-2 text-xs text-muted-foreground">{tr('admin', 'built')} {dateTime(status.build.builtAt)}</span>}
          </Row>
          {tenant && (
            <Row label={tr('admin', 'facility')}>
              <span className="font-mono">{tenant.slug}</span> <span className="text-muted-foreground">· {tenant.name}</span>
            </Row>
          )}
          <Row label={tr('admin', 'health')}>
            <Badge variant={status.healthy ? 'success' : 'destructive'}>{status.healthy ? tr('admin', 'healthy') : tr('admin', 'database_unreachable')}</Badge>
            {status.syncBusy && (
              <Badge variant="warning" className="ms-2">
                {tr('admin', 'sync_in_progress')}
              </Badge>
            )}
          </Row>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <KeyRound className="h-4 w-4 text-primary" aria-hidden /> {tr('admin', 'licence_and_activation')}
          </CardTitle>
          <CardDescription>{tr('admin', 'the_cloud_check_in_that_keeps_this_install')}</CardDescription>
        </CardHeader>
        <CardContent className="divide-y">
          <Row label={tr('admin', 'activation')}>{isLocal ? ACTIVATION_LABEL[status.activationState] : tr('admin', 'not_applicable_on_the_cloud')}</Row>
          <Row label={tr('admin', 'last_check_in')}>{isLocal ? formatWhen(status.lastCheckInAt) : '—'}</Row>
          <Row label={tr('admin', 'lock_state')}>
            <Badge variant={lock.variant}>{lock.label}</Badge>
            {status.lockState !== 'NONE' && <p className="mt-1 text-xs text-muted-foreground">{LOCK_MESSAGES[status.lockState]}</p>}
          </Row>
          <Row label={tr('admin', 'grace_period')}>
            {status.graceDays !== null ? (
              <>
                {status.graceDays} {tr('admin', 'days')}
                {status.daysUntilLock !== null && (
                  <span className={status.daysUntilLock <= 3 ? 'ms-2 text-warning' : 'ms-2 text-muted-foreground'}>
                    · {status.daysUntilLock <= 3 ? staleWarningMessage(status.daysUntilLock) : tr('admin', 'x_days_until_read_only_without_a_check_in', { daysUntilLock: status.daysUntilLock })}
                  </span>
                )}
              </>
            ) : (
              '—'
            )}
          </Row>
          {offlineBlocked && (
            <Row label={tr('admin', 'download_pin')}>
              <p className="text-xs text-muted-foreground">{tr('admin', 'offline_mode_is_not_enabled_for_this_facility')}</p>
            </Row>
          )}
          {canManage && !offlineBlocked && (
            <Row label={tr('admin', 'download_pin')}>
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  {tr('admin', 'together_with_the_facility_code')}{tenant ? ` (${tenant.slug})` : ''}{tr('admin', 'this_pin_unlocks_the_offline_installer_download')}
                </p>
                {pin && (
                  <div className="flex items-center gap-2 rounded-md border border-success/30 bg-success/5 px-3 py-2">
                    <span className="select-all font-mono text-lg tracking-[0.3em]">{pin}</span>
                    <CopyButton value={pin} label={tr('admin', 'download_pin_2')} />
                  </div>
                )}
                <Button type="button" variant="outline" size="sm" loading={regeneratePin.isPending} onClick={runRegeneratePin}>
                  <KeyRound className="h-3.5 w-3.5" /> {pin ? tr('admin', 'generate_another_pin') : tr('admin', 'generate_a_new_download_pin')}
                </Button>
              </div>
            </Row>
          )}
        </CardContent>
      </Card>

      <LicenceCard />
      {isSuperAdmin && <DataExportCard />}
      {can('audit.read') && <SupportAccessCard />}

      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <RefreshCw className="h-4 w-4 text-primary" aria-hidden /> {tr('admin', 'updates')}
          </CardTitle>
          <CardDescription>
            {isLocal
              ? tr('admin', 'the_server_asks_the_cloud_for_new_versions_on')
              : tr('admin', 'the_cloud_deployment_is_updated_by_the_platform')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="divide-y">
            <Row label={tr('admin', 'latest_known')}>
              {pending ? (
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-mono font-semibold">{pending.version}</span>
                  <Badge variant={pending.channel === 'BETA' ? 'warning' : 'default'}>{statusLabel(pending.channel)}</Badge>
                  {pending.isRequired && <Badge variant="destructive">{tr('admin', 'required')}{pending.minimumAllowedVersion ? ` · min ${pending.minimumAllowedVersion}` : ''}</Badge>}
                </span>
              ) : (
                <span className="text-muted-foreground">{isLocal ? tr('admin', 'up_to_date_as_of_the_last_check_in') : '—'}</span>
              )}
            </Row>
            {isLocal && (
              <Row label={tr('admin', 'last_update')}>
                {status.lastUpdateFailed ? (
                  <div className="space-y-1">
                    <Badge variant="destructive">{tr('admin', 'failed_and_rolled_back')}</Badge>
                    {status.lastUpdateLog && (
                      <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-2 font-mono text-[11px] leading-4">{status.lastUpdateLog}</pre>
                    )}
                  </div>
                ) : (
                  <span className="text-muted-foreground">{tr('admin', 'no_failed_update_on_record')}</span>
                )}
              </Row>
            )}
          </div>

          {pending && (
            <div className="rounded-md border bg-muted/40 p-3">
              <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{tr('admin', 'release_notes')}</p>
              <ReleaseNotes notes={pending.notes} />
            </div>
          )}

          {versionBlocked && (
            <p className="flex items-center gap-2 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4" aria-hidden /> {LOCK_MESSAGES.VERSION}
            </p>
          )}

          {isLocal && (
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" loading={checkNow.isPending} disabled={!canManage} onClick={runCheck}>
                <Search className="h-4 w-4" /> {tr('admin', 'check_for_updates_now')}
              </Button>
              {pending && (
                <Button type="button" disabled={!canManage} onClick={() => setApplyOpen(true)}>
                  <RefreshCw className="h-4 w-4" /> {tr('admin', 'update_to')} {pending.version}
                </Button>
              )}
            </div>
          )}

          {isLocal && canManage && <UpdateManagement status={status} />}
        </CardContent>
      </Card>

      <UpdateApplyDialog status={status} open={applyOpen} onOpenChange={setApplyOpen} />
    </div>
  );
}
